import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IonSkeletonText } from '@ionic/react';
import { ResponsiveLine } from '@nivo/line';
import { format } from 'date-fns';
import {
  DEFAULT_BUCKET_WINDOW_SECONDS,
  EngagementMetrics,
  EngagementMetricsLogic,
  NivoLineSeries,
} from './engagement_metrics_logic';
import { pxToRem } from '@/modules/config/constants';

interface EngagementMetricsProps {
  topicId: string;
  compact?: boolean;
}

const chartLabelStyle: React.CSSProperties = {
  fontSize: pxToRem(12),
  color: 'var(--ion-color-medium)',
  margin: '4px 0 0',
};

const chartMargin = { top: 8, right: 16, bottom: 36, left: 40 };

const chartContainerStyle: React.CSSProperties = {
  height: 220,
};

const compactChartContainerStyle: React.CSSProperties = {
  height: 110,
};

const skeletonRowStyle: React.CSSProperties = {
  width: '100%',
  height: 220,
  marginBottom: 12,
};

const TOTAL_COLOR = 'var(--ion-color-secondary)';
const DIRECT_COLOR = 'var(--ion-color-warning)';
const SNOWBALL_COLOR = 'var(--ion-color-tertiary)';
const ALL_CTR_COLOR = 'var(--ion-color-success)';

type NivoLayerProps = {
  series: {
    id: string;
    color: string;
    data: { position: { x: number | null; y: number | null } }[];
  }[];
  lineGenerator: (points: { x: number; y: number }[]) => string | null;
};

// Returns a Nivo custom lines layer where `backdropId` is drawn with a thicker
// stroke so it remains visible as a reference line behind the thinner lines on top.
const makeThickBackdropLayer =
  (backdropId: string) =>
  ({ series, lineGenerator }: NivoLayerProps) =>
    series
      .slice(0)
      .reverse()
      .map(s => (
        <path
          key={s.id}
          d={
            lineGenerator(
              s.data
                .filter(p => p.position.x !== null && p.position.y !== null)
                .map(p => p.position as { x: number; y: number })
            ) ?? ''
          }
          fill="none"
          stroke={s.color}
          strokeWidth={s.id === backdropId ? 3 : 1.5}
        />
      ));

const ctrLinesLayer = makeThickBackdropLayer('On msgs sent by all users');
const userImpressionsLinesLayer = makeThickBackdropLayer(
  'Msgs you or your downhill contacts sent'
);

const legendWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  marginBottom: 6,
};
const legendRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
const legendTextStyle: React.CSSProperties = {
  fontSize: pxToRem(12),
  lineHeight: 1.2,
  color: 'var(--ion-color-medium)',
};
const legendSwatch = (color: string): React.CSSProperties => ({
  width: 20,
  height: 3,
  borderRadius: 2,
  backgroundColor: color,
  flexShrink: 0,
});

const ChartLegend: React.FC<{ items: { label: string; color: string }[] }> = ({
  items,
}) => (
  <div style={legendWrapStyle}>
    {items.map(({ label, color }) => (
      <div key={label} style={legendRowStyle}>
        <div style={legendSwatch(color)} />
        <span style={legendTextStyle}>{label}</span>
      </div>
    ))}
  </div>
);

const SERIES_SHORT_LABELS: Record<string, string> = {
  'Msgs sent by all users': 'All',
  'Msgs you sent': 'You',
  'Msgs you or your downhill contacts sent': 'You and yours',
  'On msgs sent by all users': 'All',
  'On msgs you sent': 'You',
  'On msgs you or your downhill contacts sent': 'You and yours',
};

const tooltipStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.75)',
  color: '#fff',
  padding: '4px 8px',
  borderRadius: 4,
  fontSize: pxToRem(12),
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const tooltipRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
};

const CountTooltip: React.FC<{ point: { data: { y: number | string } } }> = ({
  point,
}) => <div style={tooltipStyle}>{String(point.data.y)}</div>;

type SlicePoint = {
  seriesId: string;
  seriesColor: string;
  data: { y: number | string };
};

const makeSliceTooltip = (formatY: (y: number) => string) => {
  const SliceTooltip: React.FC<{ slice: { points: SlicePoint[] } }> = ({
    slice,
  }) => {
    const seen = new Set<string>();
    const points = slice.points
      .slice()
      .reverse()
      .filter(p => {
        const id = String(p.seriesId);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    return (
      <div style={tooltipStyle}>
        {points.map(p => (
          <div key={p.seriesId} style={tooltipRowStyle}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: p.seriesColor,
                flexShrink: 0,
              }}
            />
            <span>
              {SERIES_SHORT_LABELS[String(p.seriesId)] ?? String(p.seriesId)}:{' '}
              {formatY(Number(p.data.y))}
            </span>
          </div>
        ))}
      </div>
    );
  };
  return SliceTooltip;
};

const ImpressionsSliceTooltip = makeSliceTooltip(y => String(y));
const CtrSliceTooltip = makeSliceTooltip(y => `${Math.round(y * 100)}%`);

const EngagementMetricsComponent: React.FC<EngagementMetricsProps> = ({
  topicId,
  compact = false,
}) => {
  const [bucketWindowSeconds] = useState<number>(DEFAULT_BUCKET_WINDOW_SECONDS);
  const [metrics, setMetrics] = useState<EngagementMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);

    EngagementMetricsLogic.fetch(
      topicId,
      bucketWindowSeconds,
      controller.signal
    )
      .then(result => {
        if (cancelled) return;
        setMetrics(result);
      })
      .catch(err => {
        if (cancelled || controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [topicId, bucketWindowSeconds]);

  const chart = useMemo(
    () => (metrics ? EngagementMetricsLogic.toChartData(metrics) : null),
    [metrics]
  );

  if (loading) {
    return (
      <>
        <IonSkeletonText animated style={skeletonRowStyle} />
        {!compact && <IonSkeletonText animated style={skeletonRowStyle} />}
        {!compact && <IonSkeletonText animated style={skeletonRowStyle} />}
      </>
    );
  }

  if (error || !chart) {
    return <p style={chartLabelStyle}>Couldn't load engagement metrics.</p>;
  }

  if (chart.impressionsSeries.total.data.length === 0) {
    return <p style={chartLabelStyle}>No one has been introduced yet.</p>;
  }

  // Compute a shared x-axis domain spanning every series so the charts
  // line up vertically and use the same daily tick positions.
  const allX: Date[] = [];
  const collect = (s: NivoLineSeries) => s.data.forEach(p => allX.push(p.x));
  collect(chart.impressionsSeries.total);
  collect(chart.impressionsSeries.direct);
  collect(chart.impressionsSeries.snowballed);
  collect(chart.ctrSeries.all);
  collect(chart.ctrSeries.direct);
  collect(chart.ctrSeries.snowballed);
  const xTimes = allX.map(d => d.getTime());
  const xMin = xTimes.length ? new Date(Math.min(...xTimes)) : undefined;
  const xMax = xTimes.length ? new Date(Math.max(...xTimes)) : undefined;

  // Build one tick Date per calendar day in the data range (local midnight).
  // Generating them explicitly guarantees at least one tick even when xMin===xMax
  // (single data point), and lets us expand xScale to cover full days so every
  // tick is within the visible range.
  const DAY_MS = 86_400_000;
  const xMinDay = xMin
    ? new Date(xMin.getFullYear(), xMin.getMonth(), xMin.getDate())
    : new Date(0);
  const xMaxDay = xMax
    ? new Date(xMax.getFullYear(), xMax.getMonth(), xMax.getDate())
    : xMinDay;
  const tickDates: Date[] = [];
  for (let d = xMinDay.getTime(); d <= xMaxDay.getTime(); d += DAY_MS) {
    tickDates.push(new Date(d));
  }

  // Thin labels so they don't overlap. Day-index 0 always gets a label
  // (0 % anything === 0), guaranteeing at least one label is always visible.
  const approxLabelPx = 52; // "May 18" ≈ 40px + gap
  // Default skipInterval to 1 (show every label) until the ResizeObserver
  // delivers a real measurement. This avoids hiding labels on first paint.
  const skipInterval =
    containerWidth > 0
      ? Math.ceil(
          tickDates.length /
            Math.max(
              1,
              Math.floor(
                (containerWidth - chartMargin.left - chartMargin.right) /
                  approxLabelPx
              )
            )
        )
      : 1;

  const axisBottom = {
    tickValues: tickDates,
    format: (value: Date | string | number) => {
      const d = value instanceof Date ? value : new Date(value);
      const dayIndex = Math.round((d.getTime() - xMinDay.getTime()) / DAY_MS);
      if (dayIndex % skipInterval !== 0) return '';
      return format(d, 'MMM d');
    },
  };
  // Expand the x domain to cover full calendar days so the first and last
  // tick marks are never clipped at the edge.
  const xScale = {
    type: 'time' as const,
    useUTC: false,
    precision: 'minute' as const,
    min: xMin ? xMinDay : undefined,
    max: xMax ? new Date(xMaxDay.getTime() + DAY_MS - 1) : undefined,
  };

  // Nivo's <Lines>/<Points> reverse the series array before painting, so the
  // FIRST series in `data` ends up on top.
  const overallImpressionsLines = [chart.impressionsSeries.total].filter(
    s => s.data.length > 0
  );
  const overallImpressionsColors = [TOTAL_COLOR];

  // User impact chart: direct on top (first), snowballed thicker behind via
  // the custom layer.
  const userImpressionsLines = [
    chart.impressionsSeries.direct,
    chart.impressionsSeries.snowballed,
  ].filter(s => s.data.length > 0);
  const userImpressionsColors = userImpressionsLines.map(s =>
    s.id === 'Msgs you or your downhill contacts sent'
      ? SNOWBALL_COLOR
      : DIRECT_COLOR
  );

  const ctrLines = [
    chart.ctrSeries.direct,
    chart.ctrSeries.snowballed,
    chart.ctrSeries.all,
  ].filter(s => s.data.length > 0);
  const ctrColors = ctrLines.map(s => {
    if (s.id === 'On msgs sent by all users') return ALL_CTR_COLOR;
    if (s.id === 'On msgs you or your downhill contacts sent')
      return SNOWBALL_COLOR;
    return DIRECT_COLOR;
  });

  const windowLabel =
    EngagementMetricsLogic.formatBucketWindow(bucketWindowSeconds);

  return (
    <div ref={containerRef}>
      {/* compact: no legend — one chart is self-evident */}
      {!compact && (
        <ChartLegend
          items={overallImpressionsLines.map((s, i) => ({
            label: s.id,
            color: overallImpressionsColors[i],
          }))}
        />
      )}
      <div style={compact ? compactChartContainerStyle : chartContainerStyle}>
        <ResponsiveLine
          data={overallImpressionsLines}
          margin={chartMargin}
          xScale={xScale}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          axisBottom={axisBottom}
          axisLeft={{ tickValues: compact ? 2 : 4 }}
          enablePoints
          pointSize={6}
          enableGridX={false}
          useMesh
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tooltip={CountTooltip as any}
          colors={overallImpressionsColors}
          curve="linear"
          animate={false}
        />
      </div>
      {!compact && userImpressionsLines.length > 0 && (
        <>
          <ChartLegend
            items={userImpressionsLines.map((s, i) => ({
              label: s.id,
              color: userImpressionsColors[i],
            }))}
          />
          <div style={chartContainerStyle}>
            <ResponsiveLine
              data={userImpressionsLines}
              margin={chartMargin}
              xScale={xScale}
              yScale={{ type: 'linear', min: 0, max: 'auto' }}
              axisBottom={axisBottom}
              axisLeft={{ tickValues: 4 }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              layers={[
                'grid',
                'markers',
                'axes',
                'areas',
                'crosshair',
                userImpressionsLinesLayer as any, // eslint-disable-line @typescript-eslint/no-explicit-any
                'points',
                'slices',
                'mesh',
                'legends',
              ]}
              enablePoints
              pointSize={6}
              enableGridX={false}
              enableSlices="x"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              sliceTooltip={ImpressionsSliceTooltip as any}
              colors={userImpressionsColors}
              curve="linear"
              animate={false}
            />
          </div>
        </>
      )}
      {!compact && (
        <>
          <p style={chartLabelStyle}>Click-through rate per {windowLabel}</p>
          <ChartLegend
            items={ctrLines.map((s, i) => ({
              label: s.id,
              color: ctrColors[i],
            }))}
          />
          <div style={chartContainerStyle}>
            <ResponsiveLine
              data={ctrLines}
              margin={chartMargin}
              xScale={xScale}
              yScale={{ type: 'linear', min: 0, max: 1 }}
              axisBottom={axisBottom}
              axisLeft={{
                tickValues: [0, 0.25, 0.5, 0.75, 1],
                format: (v: number) => `${Math.round(v * 100)}%`,
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              layers={[
                'grid',
                'markers',
                'axes',
                'areas',
                'crosshair',
                ctrLinesLayer as any, // eslint-disable-line @typescript-eslint/no-explicit-any
                'points',
                'slices',
                'mesh',
                'legends',
              ]}
              enablePoints
              pointSize={6}
              enableGridX={false}
              enableSlices="x"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              sliceTooltip={CtrSliceTooltip as any}
              colors={ctrColors}
              animate={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EngagementMetricsComponent;
